import React, { useCallback, useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronLeft, ChevronRight, Images, Maximize2, Pause, Play, Presentation, X } from "lucide-react";
import { Btn, Overline, Panel } from "../ui";
import { CONCEPTS } from "../../lib/constants";
import { useI18n } from "../../lib/i18n";

const RISE_FROM = { opacity: 0, y: 14 };
const RISE_TO = { opacity: 1, y: 0 };
const FADE_FROM = { opacity: 0 };
const FADE_TO = { opacity: 1 };
const ZOOM_FROM = { opacity: 0, scale: 0.985 };
const ZOOM_TO = { opacity: 1, scale: 1 };
const ZOOM_EASE = { duration: 0.35 };

export default function DesignGallery() {
  const { t } = useI18n();
  const [open, setOpen] = useState(null);
  const [auto, setAuto] = useState(false);

  const step = useCallback((dir) => {
    setOpen((i) => (i === null ? null : (i + dir + CONCEPTS.length) % CONCEPTS.length));
  }, []);

  useEffect(() => {
    if (open === null) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(null);
      if (e.key === "ArrowRight") step(1);
      if (e.key === "ArrowLeft") step(-1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, step]);

  useEffect(() => {
    if (open === null || !auto) return undefined;
    const id = setInterval(() => step(1), 6000);
    return () => clearInterval(id);
  }, [open, auto, step]);

  const startPresenting = () => {
    setAuto(true);
    setOpen(0);
  };

  return (
    <div className="space-y-6" data-testid="gallery-screen">
      <div>
        <Overline>
          {t("gal.screen")} 10 · {t("nav.gallery")}
        </Overline>
        <h1 className="mt-1.5 font-heading text-3xl font-light tracking-tight text-slate-100 sm:text-4xl">
          {t("gal.title")}
        </h1>
        <p className="mt-2 max-w-2xl text-sm text-slate-400">{t("gal.sub")}</p>
      </div>

      <Panel
        title={t("gal.boards")}
        subtitle={t("gal.count", { n: CONCEPTS.length })}
        icon={Images}
        accent="purple"
        testid="gallery-panel"
        actions={
          <Btn variant="purple" testid="gallery-present-btn" onClick={startPresenting} className="whitespace-nowrap">
            <Presentation size={15} /> {t("gal.present")}
          </Btn>
        }
      >
        <p className="mb-5 font-mono text-[11px] text-slate-400">{t("gal.hint")}</p>
        <div className="grid grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-3">
          {CONCEPTS.map((c, i) => (
            <motion.button
              key={c.id}
              data-testid={`concept-card-${c.num}`}
              onClick={() => {
                setAuto(false);
                setOpen(i);
              }}
              initial={RISE_FROM}
              animate={RISE_TO}
              transition={{ duration: 0.45, delay: i * 0.06 }}
              className="group overflow-hidden rounded-xl border border-edge raise text-left transition-colors duration-200 hover:border-aegis-purple/40"
            >
              <div className="relative aspect-[3/2] overflow-hidden border-b border-edge">
                <img
                  src={c.src}
                  alt={t(`${c.key}.title`)}
                  loading="lazy"
                  className="h-full w-full object-cover object-top transition-transform duration-[900ms] ease-out group-hover:scale-[1.03]"
                />
                <span className="absolute left-3 top-3 rounded-md border border-edgeStrong bg-void/80 px-2 py-0.5 font-mono text-[10px] text-slate-300">
                  {t("gal.screen")} {c.num}
                </span>
                <span className="absolute right-3 top-3 grid h-7 w-7 place-items-center rounded-md border border-edgeStrong bg-void/80 text-slate-300 opacity-0 transition-opacity duration-200 group-hover:opacity-100">
                  <Maximize2 size={13} />
                </span>
              </div>
              <div className="p-4">
                <p className="font-heading text-[15px] font-medium text-slate-100">{t(`${c.key}.title`)}</p>
                <p className="mt-1.5 text-[12px] leading-relaxed text-slate-400">{t(`${c.key}.cap`)}</p>
              </div>
            </motion.button>
          ))}
        </div>
      </Panel>

      <AnimatePresence>
        {open !== null && (
          <motion.div
            initial={FADE_FROM}
            animate={FADE_TO}
            exit={FADE_FROM}
            className="fixed inset-0 z-[70] flex flex-col bg-void/95 backdrop-blur-sm"
            data-testid="concept-lightbox"
          >
            <div className="flex shrink-0 items-center justify-between gap-4 border-b border-edge px-5 py-3">
              <div className="min-w-0">
                <Overline>
                  {t("gal.screen")} {CONCEPTS[open].num} · {open + 1} / {CONCEPTS.length}
                </Overline>
                <p className="truncate font-heading text-[15px] text-slate-100" data-testid="lightbox-title">
                  {t(`${CONCEPTS[open].key}.title`)}
                </p>
              </div>
              <div className="flex shrink-0 items-center gap-2">
                <Btn
                  variant={auto ? "amber" : "ghost"}
                  testid="lightbox-auto-btn"
                  onClick={() => setAuto((a) => !a)}
                  className="!px-3"
                >
                  {auto ? <Pause size={14} /> : <Play size={14} />}
                  <span className="hidden sm:inline">{t("gal.auto")}</span>
                </Btn>
                <Btn variant="ghost" testid="lightbox-close" onClick={() => setOpen(null)} className="!px-3">
                  <X size={15} /> <span className="hidden sm:inline">{t("gal.exit")}</span>
                </Btn>
              </div>
            </div>

            <div className="relative flex min-h-0 flex-1 items-center justify-center px-4 py-5">
              <button
                onClick={() => step(-1)}
                data-testid="lightbox-prev"
                className="absolute left-3 z-10 grid h-11 w-11 place-items-center rounded-full border border-edge bg-panel/80 text-slate-300 transition-colors hover:border-aegis-cyan/40 hover:text-aegis-cyan"
              >
                <ChevronLeft size={20} />
              </button>
              <motion.img
                key={CONCEPTS[open].id}
                src={CONCEPTS[open].src}
                alt={t(`${CONCEPTS[open].key}.title`)}
                initial={ZOOM_FROM}
                animate={ZOOM_TO}
                transition={ZOOM_EASE}
                className="max-h-full max-w-[92%] rounded-xl border border-edge object-contain"
                data-testid="lightbox-image"
              />
              <button
                onClick={() => step(1)}
                data-testid="lightbox-next"
                className="absolute right-3 z-10 grid h-11 w-11 place-items-center rounded-full border border-edge bg-panel/80 text-slate-300 transition-colors hover:border-aegis-cyan/40 hover:text-aegis-cyan"
              >
                <ChevronRight size={20} />
              </button>
            </div>

            <div className="shrink-0 border-t border-edge px-5 py-3">
              <p className="mx-auto max-w-4xl text-center text-[12px] leading-relaxed text-slate-400">
                {t(`${CONCEPTS[open].key}.cap`)}
              </p>
              <div className="mt-3 flex justify-center gap-1.5">
                {CONCEPTS.map((c, i) => (
                  <button
                    key={c.id}
                    onClick={() => setOpen(i)}
                    data-testid={`lightbox-dot-${c.num}`}
                    className={`h-1.5 rounded-full transition-all duration-300 ${
                      i === open ? "w-7 bg-aegis-cyan" : "w-1.5 bg-slate-600 hover:bg-slate-400"
                    }`}
                  />
                ))}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
